import { BatchResourcesAclDc, Permissions, ResourceType, RolePermissionDc } from "@evergis/api";
import { ChipAutoComplete, ChipAutoCompleteData, Description, H3, Icon, useToggle } from "@evergis/ui";
import React, { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUsername } from "../../../../ducks/user";
import { isConfigure } from "../../../../utils/permissions";
import { RestrictedWidthContainer } from "../../DialogComponents/styled";
import { AccessControl } from "../components/AccessControl";
import { CopyPublicResourceLink } from "../components/CopyPublicResourceLink";
import { getAclRole, getNameByRole } from "../getAclRole";
import { useTotal } from "../hooks/useTotal";
import { ProjectAccessDescription, ProjectControlsPanel } from "../ProjectAccess/styled";
import { CommonSettingsControls, CommonSettingsTitleContainer } from "../styled";
import { AccessRoleGroup, LayerStructureInfo } from "../types";
import { useUsersAndRoles } from "../useUsersAndRoles";
import { useStructure } from "./hooks/useStructure";
import { LayerStructure } from "./LayerStructure";
import { LayerDependenciesContainer, ToggleContainer } from "./styled";

export type LayerAccessControlProps = {
  layerStructure: LayerStructureInfo;
  setStructure?: (structure: LayerStructureInfo) => void;
  setUpdateData?: (updateData: BatchResourcesAclDc) => void;
  title?: ReactNode;
  layerName?: string;
};

// eslint-disable-next-line max-lines-per-function
export const LayerAccessControl: FC<LayerAccessControlProps> = ({
  layerStructure,
  setUpdateData,
  setStructure,
  layerName,
}) => {
  const { t } = useTranslation("dialogs");

  const currentUser: string = useSelector(getUsername);

  const { users, usersAndRolesLoading, autoCompleteProps, pickedUsers, setPickedUsers } = useUsersAndRoles();
  const [activeUser, setActiveUser] = useState<string>("");
  const loading = usersAndRolesLoading;
  const [expanded, toggleExpanded] = useToggle(false);
  const {
    structure,
    updateData,
    partial,
    onBulkUpdate,
    onUserPermissionsInit,
    onUserPermissionsRemove,
    onUpdateEntity,
    enrichAclData,
    checkedAll,
  } = useStructure(layerStructure, getAclRole(activeUser, users), pickedUsers, loading);

  useEffect(() => {
    if (setUpdateData) {
      setUpdateData(updateData);
    }
  }, [updateData, setUpdateData]);

  useEffect(() => {
    if (setStructure) {
      setStructure(structure);
    }
  }, [structure, setStructure]);

  const { onUpdateTotal } = useTotal(onBulkUpdate, checkedAll, partial, activeUser);

  // Обновление списка пользователей, которые необходимо отразить в списке чипов,
  // при изменении данных в структуре слоя (acl). При этом показываем чипы только для тех пользователей,
  // для которых в acl вложенных сущностей есть данные и при условии, что сущности эти пользователи
  // имеют право конфигурировать
  useEffect(() => {
    if (!structure || !users || !currentUser) {
      return;
    }

    let processedUsers = [];

    const checkEntityUser = (item: RolePermissionDc, entityPermissions: Permissions) => {
      const { role } = item;
      const userName = getNameByRole(role, users);

      if (processedUsers.includes(userName)) {
        return;
      }

      if (users.some(user => user.aclRole === role) && `__${currentUser}` !== role && isConfigure(entityPermissions)) {
        if (userName === AccessRoleGroup.Public) {
          processedUsers = [userName, ...processedUsers];
        } else {
          processedUsers = [...processedUsers, userName];
        }
      }
    };

    structure.tables.forEach(table => {
      table.acl.data.forEach(tableAclItem => {
        checkEntityUser(tableAclItem, table.permissions);
      });

      table.layers.forEach(layer => {
        layer.acl.data.forEach(layerAclItem => {
          checkEntityUser(layerAclItem, layer.permissions);
        });
      });
    });

    setPickedUsers(processedUsers);
  }, [structure, users, currentUser]);

  const getTextName = useCallback((name: string) => (name === AccessRoleGroup.Public ? t("access.public") : name), [t]);
  const readOnly = activeUser === AccessRoleGroup.Public;

  return (
    <>
      <RestrictedWidthContainer>
        <ProjectAccessDescription>{t("access.accessDescription")}</ProjectAccessDescription>
        <ChipAutoComplete
          loading={loading}
          activeItem={activeUser}
          items={users as ChipAutoCompleteData[]}
          activeItems={pickedUsers}
          onSelectItem={(name: string) => {
            setActiveUser(name);
            onUserPermissionsInit(getAclRole(name, users));
          }}
          onRemoveItem={(name: string) => {
            setActiveUser("");
            onUserPermissionsRemove(getAclRole(name, users));
          }}
          onActiveItem={(name: string) => {
            setActiveUser(name);
            enrichAclData(getAclRole(name, users));
          }}
          slideButtonTitle={t("access.slide")}
          listButtonTitle={t("access.list")}
          placeholder={t("access.chooseUserName")}
          searchPlaceholder={t("access.search")}
          getTextName={getTextName}
          autoCompleteProps={autoCompleteProps}
        />
        {activeUser && (
          <>
            <CommonSettingsTitleContainer>
              <H3>{t("access.accessTitle")}</H3>
              <CommonSettingsControls>
                <AccessControl
                  type="read"
                  active={checkedAll?.read || partial?.read}
                  clickable={!(checkedAll?.read || partial?.read)}
                  onClick={() => !(checkedAll?.read || partial?.read) && onUpdateTotal("read")}
                  partial={partial?.read}
                />
                {!readOnly && (
                  <>
                    <AccessControl
                      type="write"
                      active={checkedAll?.write || partial?.write}
                      onClick={() => onUpdateTotal("write")}
                      partial={partial?.write}
                    />
                    <AccessControl
                      type="configure"
                      active={checkedAll?.configure || partial?.configure}
                      onClick={() => onUpdateTotal("configure")}
                      partial={partial?.configure}
                    />
                  </>
                )}
              </CommonSettingsControls>
            </CommonSettingsTitleContainer>
            <ProjectControlsPanel>
              <ToggleContainer toggled={expanded} onClick={toggleExpanded}>
                <Description>{t("access.extraSettings")}</Description>
                <Icon kind={"expand"} />
              </ToggleContainer>
              {layerName && (
                <CopyPublicResourceLink
                  resourceType={ResourceType.Layer}
                  isPublicRole={activeUser === AccessRoleGroup.Public}
                  name={layerName}
                />
              )}
            </ProjectControlsPanel>
          </>
        )}
      </RestrictedWidthContainer>
      {activeUser && expanded && (
        <LayerDependenciesContainer>
          <RestrictedWidthContainer>
            <LayerStructure
              readOnly={readOnly}
              activeAclRole={getAclRole(activeUser, users)}
              tables={structure.tables}
              onUpdateEntity={onUpdateEntity}
            />
          </RestrictedWidthContainer>
        </LayerDependenciesContainer>
      )}
    </>
  );
};
